import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { 
  Save, Plus, Trash2, GripVertical, Settings, 
  FileText, Shield, Home, CreditCard, Paintbrush
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD_CATEGORIES = [
  { 
    id: 'project_status', 
    label: 'Job Statuses', 
    icon: Settings,
    description: 'Status options for jobs (used in Create Job form)'
  },
  { 
    id: 'job_type', 
    label: 'Job Types', 
    icon: FileText,
    description: 'Types of jobs/projects'
  },
  { 
    id: 'warranty', 
    label: 'Warranty Options', 
    icon: Shield,
    description: 'Available warranty periods for jobs'
  },
  { 
    id: 'product_used', 
    label: 'Products', 
    icon: Paintbrush,
    description: 'Products that can be used on jobs'
  },
  { 
    id: 'hoa_info', 
    label: 'HOA Status Options', 
    icon: Home,
    description: 'HOA approval status options'
  },
  { 
    id: 'payment_plan', 
    label: 'Payment Plans', 
    icon: CreditCard,
    description: 'Available payment plan options'
  }
];

export const JobFieldSettings = () => {
  const [fieldOptions, setFieldOptions] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [newValues, setNewValues] = useState({});

  useEffect(() => {
    fetchFieldOptions();
  }, []);

  const fetchFieldOptions = async () => {
    try {
      const response = await axios.get(`${API}/settings/job-field-options`);
      setFieldOptions(response.data);
      // Initialize new value inputs
      const initNewValues = {};
      FIELD_CATEGORIES.forEach(cat => {
        initNewValues[cat.id] = '';
      });
      setNewValues(initNewValues);
    } catch (error) {
      console.error('Error fetching field options:', error);
      toast.error('Failed to load field options');
    } finally {
      setLoading(false);
    }
  };

  const handleAddOption = (categoryId) => {
    const value = newValues[categoryId]?.trim();
    if (!value) return;
    
    // Check for duplicates
    if (fieldOptions[categoryId]?.includes(value)) {
      toast.error('This option already exists');
      return;
    }

    setFieldOptions(prev => ({
      ...prev,
      [categoryId]: [...(prev[categoryId] || []), value]
    }));
    setNewValues(prev => ({ ...prev, [categoryId]: '' }));
    setHasChanges(true);
  };

  const handleRemoveOption = (categoryId, index) => {
    setFieldOptions(prev => ({
      ...prev,
      [categoryId]: prev[categoryId].filter((_, i) => i !== index)
    }));
    setHasChanges(true);
  };

  const handleMoveOption = (categoryId, fromIndex, toIndex) => {
    if (toIndex < 0 || toIndex >= fieldOptions[categoryId].length) return;
    
    const newArray = [...fieldOptions[categoryId]];
    const [moved] = newArray.splice(fromIndex, 1);
    newArray.splice(toIndex, 0, moved);
    
    setFieldOptions(prev => ({
      ...prev,
      [categoryId]: newArray
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/settings/job-field-options`, fieldOptions);
      toast.success('Field options saved successfully');
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving field options:', error);
      toast.error('Failed to save field options');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Job Field Settings</h1>
            <p className="text-muted-foreground">
              Configure dropdown options for job fields
            </p>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-400">
                Unsaved Changes
              </Badge>
            )}
            <Button 
              onClick={handleSave}
              disabled={saving || !hasChanges}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>

        {/* Field Categories Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {FIELD_CATEGORIES.map(category => {
            const Icon = category.icon;
            const options = fieldOptions[category.id] || [];
            
            return (
              <Card key={category.id} className="bg-slate-900 border-slate-800">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Icon className="h-4 w-4 text-blue-400" />
                    {category.label}
                  </CardTitle>
                  <CardDescription className="text-xs">
                    {category.description}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3">
                  {/* Add New Option */}
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add new option..."
                      value={newValues[category.id] || ''}
                      onChange={(e) => setNewValues(prev => ({ 
                        ...prev, 
                        [category.id]: e.target.value 
                      }))}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddOption(category.id);
                        }
                      }}
                      className="flex-1 h-8 text-sm bg-slate-800 border-slate-700"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleAddOption(category.id)}
                      disabled={!newValues[category.id]?.trim()}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Options List */}
                  <div className="space-y-1 max-h-64 overflow-y-auto">
                    {options.length === 0 ? (
                      <p className="text-xs text-slate-500 text-center py-4">
                        No options configured
                      </p>
                    ) : (
                      options.map((option, index) => (
                        <div
                          key={index}
                          className="flex items-center gap-2 p-2 bg-slate-800 rounded-lg group"
                        >
                          <div className="flex flex-col gap-0.5">
                            <button
                              onClick={() => handleMoveOption(category.id, index, index - 1)}
                              disabled={index === 0}
                              className="text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <GripVertical className="h-3 w-3 rotate-180" />
                            </button>
                            <button
                              onClick={() => handleMoveOption(category.id, index, index + 1)}
                              disabled={index === options.length - 1}
                              className="text-slate-500 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed"
                            >
                              <GripVertical className="h-3 w-3" />
                            </button>
                          </div>
                          <span className="flex-1 text-sm">{option}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            onClick={() => handleRemoveOption(category.id, index)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Count */}
                  <div className="text-xs text-slate-500 text-right">
                    {options.length} option{options.length !== 1 ? 's' : ''}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Card */}
        <Card className="bg-blue-950/30 border-blue-800/50">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <Settings className="h-5 w-5 text-blue-400 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-blue-300">How it works</p>
                <p className="text-xs text-blue-400/70 mt-1">
                  The options you configure here will appear as dropdown choices in the Job Info tab. 
                  Changes are applied immediately after saving. You can reorder options by using the 
                  up/down arrows, and remove options you no longer need.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default JobFieldSettings;
